import { useCallback, useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton } from '@mui/material';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { DeleteConfirmationDialog } from '../../components/common/dialog/DeleteConfirmationDialog';
import FileDragDrop, { type SetSelectedFiles, type ExtendedFile } from '../../components/common/files/FileDragDrop';
import Loadable from '../../components/common/loadable';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls } from '../../utils/url';

type VirtualBackground = {
    fileKey: string;
    url: string;
    isTemporary?: boolean; // If it's just a temporary base 64 image url.
};

const BackgroundsGrid = styled(Box)`
    box-sizing: border-box;
    display: flex;
    flex-flow: wrap;
    width: 100%
    margin-top: 0px;
    gap: 8px;
`;

const BackgroundTile = styled(Box)`
    flex-basis: calc(33.3333% - 8px);
    -webkit-box-flex: 0;
    flex-grow: 0;
    max-width: calc(33.3333% - 8px);
    img {
        width: 100%;
        height: auto;
    }
`;

const BackgroundTileInner = styled(Loadable)`
    position: relative;
    height: 100%;
    background-color: #dddddd;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    .del-icon {
        position: absolute;
        top: 8px;
        right: 8px;
    }
    border-radius: 4px;
`;

const TileButton = styled(IconButton)`
    background-color: rgba(255, 255, 255, 0.75);
    &:hover {
        background-color: rgba(255, 255, 255, 0.95);
    }
`;

const NoticeContainer = styled(Box)`
    display: flex;
    font-size: 14px;
    svg {
        margin-right: 4px;
        color: ${(props) => props.theme.colors.primary.main};
    }
`;

const convertFileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
    });

const VirtualBackgrounds = () => {
    const { data: schoolInfo } = useSchoolInfo();
    const schoolUuid = schoolInfo?.uuid as string;
    const { uuid: userUuid } = useContainer(AdminAuthContainer);
    const { api } = getUrls();
    const { t } = useTranslation('admin_settings');
    const [virtualBackgrounds, setVirtualBackgrounds] = useState<VirtualBackground[]>([]);
    const [loadingVirtualBackgrounds, setLoadingVirtualBackgrounds] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [selectedForDeletion, setSelectedForDeletion] = useState<VirtualBackground | undefined>();
    const [isDeleting, setIsDeleting] = useState<string[]>([]); // Array of items currently being deleted.
    const { enqueueSnackbar } = useSnackbar();
    const maxFileSizeMB = 20; // Max file size is 20MB.

    const getVirtualBackgrounds = useCallback(
        async (showLoading: boolean = true) => {
            if (showLoading) {
                setLoadingVirtualBackgrounds(true);
            }
            try {
                const response = await fetch(`${api}/v1/virtual-background/s3-get-urls`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getUserUuidHeaders(userUuid) },
                    body: JSON.stringify({
                        userUuid,
                        type: 'organisation'
                    })
                });

                const { virtualBackgrounds } = await response.json();
                setVirtualBackgrounds(virtualBackgrounds);
            } catch (error) {
                console.error('Failed to fetch virtual backgrounds:', error);
            } finally {
                if (showLoading) {
                    setLoadingVirtualBackgrounds(false);
                }
            }
        },
        [api, userUuid]
    );

    useEffect(() => {
        getVirtualBackgrounds();
    }, [getVirtualBackgrounds]);

    const handleDeleteConfirmationClose = useCallback(() => {
        setShowDeleteConfirmation(false);
    }, []);

    const deleteVirtualBackground = useCallback(
        async (key: string) => {
            handleDeleteConfirmationClose();
            setIsDeleting((prevIsDeleting) => [...prevIsDeleting, key]);

            try {
                const response = await fetch(`${api}/v1/virtual-background/s3-delete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getUserUuidHeaders(userUuid) },
                    body: JSON.stringify({
                        key
                    })
                });

                if (response.status === 200) {
                    setVirtualBackgrounds((prevVirtualBackgrounds) =>
                        prevVirtualBackgrounds.filter((vb) => vb.fileKey !== key)
                    );
                } else {
                    console.error('Unexpected response status when deleting');
                    enqueueSnackbar(t('virtual-backgrounds.failed-to-delete'), { variant: 'error' });
                }
            } catch (e) {
                console.error(e);
                enqueueSnackbar(t('virtual-backgrounds.failed-to-delete'), { variant: 'error' });
            } finally {
                setIsDeleting((prevIsDeleting) => prevIsDeleting.filter((dKey) => dKey !== key));
            }
        },
        [api, userUuid, handleDeleteConfirmationClose, enqueueSnackbar, t]
    );

    const showDeleteBackgroundModal = useCallback((virtualBg: VirtualBackground) => {
        setSelectedForDeletion(virtualBg);
        setShowDeleteConfirmation(true);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!selectedForDeletion) {
            throw new Error('Nothing selected for deletion');
        }

        setSelectedForDeletion(undefined);

        await deleteVirtualBackground(selectedForDeletion.fileKey);
    }, [selectedForDeletion, deleteVirtualBackground]);

    const uploadFiles = async (files: ExtendedFile[], setFiles: SetSelectedFiles) => {
        // Request signed URLs for all files
        const response = await fetch(`${api}/v1/virtual-background/s3-put-urls`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getUserUuidHeaders(userUuid) },
            body: JSON.stringify({ userUuid, type: 'organisation', fileNames: files.map((file) => file.file.name) })
        });

        const { signedUrls } = await response.json();

        // Create upload promises and register onprogress hook to report upload progress.
        const uploadPromises = signedUrls.map(
            ({ fileName, signedUrl }, index) =>
                new Promise((resolve, reject) => {
                    const file = files[index];
                    const xhr = new XMLHttpRequest();

                    xhr.open('PUT', signedUrl, true);
                    xhr.setRequestHeader('Content-Type', file.file.type);

                    // Track upload progress
                    xhr.upload.onprogress = (event) => {
                        if (event.lengthComputable) {
                            const percentComplete = Math.round((event.loaded / event.total) * 100);
                            // Use functional state update to ensure correct state tracking
                            setFiles((prevFiles) =>
                                prevFiles.map((f) =>
                                    f.file.name === file.file.name
                                        ? { ...f, uploading: percentComplete < 100, progress: percentComplete }
                                        : f
                                )
                            );
                        }
                    };

                    xhr.onload = async () => {
                        if (xhr.status === 200) {
                            // Handle success
                            const base64Url = await convertFileToBase64(file.file);

                            setVirtualBackgrounds((prevBackgrounds) => [
                                ...prevBackgrounds,
                                { fileKey: fileName, url: base64Url, isTemporary: true }
                            ]);

                            // Remove file from files list
                            setFiles((prevFiles) => prevFiles.filter((f) => f.file.name !== file.file.name));

                            resolve(true);
                        } else {
                            console.error(`Upload failed for ${fileName}`);
                            enqueueSnackbar(t('virtual-backgrounds.file-upload-failed', { fileName }), {
                                variant: 'error'
                            });
                            setFiles((prevFiles) =>
                                prevFiles.map((f) =>
                                    f.file.name === fileName ? { ...f, failed: true, uploading: false } : f
                                )
                            );
                            resolve(false);
                        }
                    };

                    // Handle errors
                    xhr.onerror = () => reject(new Error(`Network error for ${fileName}`));

                    // Start the upload
                    xhr.send(file.file);
                })
        );

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);

        // 🔄 Refresh backgrounds after all uploads are done but wait 10 seconds to give resize lambda a chance to run.
        window.setTimeout(() => getVirtualBackgrounds(false), 10000);
    };

    const onFilesAdded = (files: ExtendedFile[], setFiles: SetSelectedFiles) => {
        const validFiles = files.filter((file) => {
            if (file.file.size > maxFileSizeMB * 1024 * 1024) {
                enqueueSnackbar(t('virtual-backgrounds.file-too-large', { maxSize: maxFileSizeMB }), {
                    variant: 'error'
                });
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) {
            return; // No valid files, exit early
        }

        const uploadingFiles = validFiles.map((file) => ({
            ...file,
            uploading: true,
            progress: 0
        }));

        setFiles(uploadingFiles);
        uploadFiles(uploadingFiles, setFiles);
    };

    if (!schoolUuid) {
        return null;
    }

    const acceptedFileTypes = ['image/png', 'image/jpeg', 'image/webp'];

    return (
        <Box sx={{ marginBottom: '32px' }}>
            <DeleteConfirmationDialog
                title={t('virtual-backgrounds.delete-dialog-title')}
                open={showDeleteConfirmation}
                onConfirm={handleDelete}
                onClose={handleDeleteConfirmationClose}
            />
            <Loadable isLoading={loadingVirtualBackgrounds}>
                <Box sx={{ marginTop: '16px' }}>
                    <NoticeContainer>
                        <InfoOutlinedIcon fontSize="small" />
                        {t('virtual-backgrounds.recommended-size')}
                    </NoticeContainer>
                    <NoticeContainer>
                        <InfoOutlinedIcon fontSize="small" />
                        {t('virtual-backgrounds.warning-performance')}
                    </NoticeContainer>
                </Box>
                <FileDragDrop
                    acceptedFileTypes={acceptedFileTypes}
                    acceptedFileTypesText={t('virtual-backgrounds.supported-file-types')}
                    onFilesAdded={onFilesAdded}
                />

                <BackgroundsGrid>
                    {virtualBackgrounds.map((virtualBg) => (
                        <BackgroundTile key={virtualBg.fileKey}>
                            <BackgroundTileInner isLoading={isDeleting.some((dKey) => dKey === virtualBg.fileKey)}>
                                {!virtualBg.isTemporary && (
                                    <TileButton
                                        aria-label={t('virtual-backgrounds.delete')}
                                        className="del-icon"
                                        onClick={() => showDeleteBackgroundModal(virtualBg)}
                                    >
                                        <DeleteIcon />
                                    </TileButton>
                                )}
                                <img src={virtualBg.url} crossOrigin="anonymous" />
                            </BackgroundTileInner>
                        </BackgroundTile>
                    ))}
                </BackgroundsGrid>
            </Loadable>
        </Box>
    );
};

export default VirtualBackgrounds;
